/**
 * Guía: Cómo se recorre este panel.
 *
 * Contenido derivado del capítulo **42** del manual («El panel no tiene
 * portada») y de la estructura del menú en `src/config/navigation.tsx`.
 *
 * Es la primera guía que se ofrece a cualquiera, porque explica lo único que
 * no se deduce mirando: **que el menú está partido en dos mitades y que la
 * de abajo depende del país seleccionado**. Un operador que no lo sabe
 * interpreta una pantalla vacía como «no hay datos» cuando lo que pasa es
 * que no ha elegido país.
 */

import type { TourFlow } from '../types';

export const c42Orientacion: TourFlow = {
  id: 'admin.orientation',
  chapter: '42',
  title: 'Cómo se recorre este panel',
  summary: 'El menú en dos mitades, el país que manda sobre todo y dónde buscar.',
  estimatedMin: 2,
  steps: [
    {
      id: 'intro',
      title: 'Este panel no tiene portada',
      body:
        'Al entrar no verás un resumen: la raíz te lleva directamente a la primera ' +
        'pantalla de tu menú, que es distinta para cada rol. Finanzas aterriza en el panel ' +
        'financiero; un agente de soporte, en sus tickets. Lo que ves en el menú es ' +
        'exactamente lo que tu rol puede abrir.',
    },
    {
      id: 'global',
      title: 'Arriba: la plataforma entera',
      body:
        'Estas entradas no dependen de ningún país. Son la configuración global de ' +
        '121Pass —monedas, idiomas, versiones de la app, parámetros— y lo que se toque ' +
        'aquí aplica en todos los mercados a la vez.',
      target: 'admin.sidebar.global',
      placement: 'right',
      optionalTarget: true,
    },
    {
      id: 'country-section',
      title: 'Abajo: la operación de un país',
      body:
        'Todo lo que hay bajo «Operación por País» —tickets, compañías, clientes, ' +
        'finanzas— muestra únicamente datos del país seleccionado. Es el filtro más ' +
        'importante del panel y no aparece dentro de cada pantalla: vive aquí, en el menú.',
      target: 'admin.sidebar.countrySection',
      requiresCountryNav: true,
      placement: 'right',
    },
    {
      id: 'country-selector',
      title: 'Cambiar de país',
      body:
        'Como Super Admin puedes moverte entre países desde este selector. Al cambiarlo ' +
        'cambian de golpe todas las pantallas de abajo: si una bandeja aparece vacía, ' +
        'comprueba primero qué país tienes puesto.',
      target: 'admin.sidebar.countrySelector',
      roles: ['SUPER_ADMIN'],
      requiresCountryNav: true,
      placement: 'right',
    },
    {
      id: 'country-fixed',
      title: 'Tu país está fijado',
      body:
        'Tu cuenta trabaja sobre un país concreto, el que tiene tu ficha de colaborador, ' +
        'y no hay selector que cambiarlo. Todo lo que veas en las pantallas de abajo es ' +
        'de ese mercado. Si necesitas operar en otro, pídelo al equipo de plataforma.',
      roles: ['SUPPORT_LEAD', 'SUPPORT_AGENT', 'SALES_MANAGER', 'FINANCE', 'MARKETING'],
      requiresCountryNav: true,
    },
    {
      id: 'search',
      title: 'Ir a cualquier pantalla sin buscarla',
      body:
        'Este buscador abre la paleta de comandos —también con Ctrl+K, o Cmd+K en Mac— y ' +
        'salta a cualquier pantalla de tu menú escribiendo su nombre. Es más rápido que ' +
        'recorrer la barra lateral.',
      target: 'admin.header.palette',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'breadcrumbs',
      title: 'Dónde estás',
      body:
        'La ruta de arriba te dice en qué pantalla estás y desde dónde llegaste. En las ' +
        'pantallas con detalle —una compañía, una sucursal— es la forma de volver sin ' +
        'perder el filtro que traías.',
      target: 'admin.breadcrumbs',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'outro',
      title: 'El botón de ayuda se queda ahí',
      body:
        'Cada pantalla con guía propia la ofrece desde la esquina inferior derecha. Puedes ' +
        'salir de una guía en cualquier momento con Escape: no bloquea nada de lo que ' +
        'estés haciendo.',
    },
  ],
};
